import React, { FC, ReactNode, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { GenerateNotificationVariantsRequest } from '@pushly/models/lib/structs'
import { createBrandVoiceOwner, BrandVoiceProvider } from '../../../modules/ai/brand-voice'
import type { BrandVoiceOwnerDeps, GenerateVariantsAnswer } from '../../../modules/ai/brand-voice'
import { DomainService } from '../../../services/domain'
import { NotificationService } from '../../../services/notification'
import { registerBrandVoiceOwner } from './brand-voice-owner-registry'

/**
 * Turns the services into the owner's dependencies. Pure: it takes the services as parameters, so a test hands it
 * fakes and the owner never learns where its requests go.
 */
export const brandVoiceDepsFromServices = (
    notifications: Pick<NotificationService, 'generateNotificationVariants'>,
    domains: Pick<DomainService, 'fetchSegmentsByDomainId'>,
    domainId: number,
): BrandVoiceOwnerDeps => ({
    domainId,
    fetchSegments: async (id) => {
        const res = await domains.fetchSegmentsByDomainId(id, { query: { limit: 200 } })

        return res.ok
            ? {
                  ok: true,
                  data: (res.data ?? []).map((segment) => ({
                      id: segment.id,
                      name: segment.name,
                      isDefault: !!segment.isDefault,
                  })),
              }
            : { ok: false }
    },
    generateVariants: async (id, request) => {
        const res = await notifications.generateNotificationVariants(
            id,
            GenerateNotificationVariantsRequest.from(request),
            {
                showLoadingScreen: false,
            },
        )

        if (!res.ok || !res.data) {
            return { ok: false }
        }

        // The call skips case conversion, so the page error arrives as the API wrote it, in snake case, whatever its
        // declared type says.
        const error = res.data.error as unknown as GenerateVariantsAnswer['error']

        return {
            ok: true,
            data: { variants: res.data.variants ?? [], error: error ?? null, policy_error: res.data.policyError },
        }
    },
})

interface IBrandVoiceBridge {
    domainId: number
    children: ReactNode
}

/**
 * The one place Brand Voice's new code meets the legacy service locator. The owner and everything under
 * `src/modules/ai/brand-voice` take what they need as parameters, and this supplies them. The owner is built once per
 * domain: a domain switch remounts the bridge by its key, so opening another domain starts clean.
 */
export const BrandVoiceBridge: FC<IBrandVoiceBridge> = ({ domainId, children }) => {
    // The owner holds state and requests in flight, so React must not rebuild it on its own.
    const [owner] = useState(() =>
        createBrandVoiceOwner(
            brandVoiceDepsFromServices(Container.get(NotificationService), Container.get(DomainService), domainId),
        ),
    )

    // The console helpers act on the owner that is open.
    useEffect(() => registerBrandVoiceOwner(owner), [owner])

    return <BrandVoiceProvider owner={owner}>{children}</BrandVoiceProvider>
}
